/**
 * Module: breadcrumb-adapter.ts
 * Description: This module is responsible for adapting breadcrumb data for the application.
 * It processes breadcrumb content, configuration, and page information to generate a structured breadcrumb object.
 *
 * Logging: Added logging for service events and errors using `logAdapterEvent` and `logError`
 * from the `logger.ts` module. This logging is intended for production debugging.
 */

import { ComponentNames } from "../../constant";
import { logAdapterEvent, logError } from "../../helper/logger"; // Importing logger functions

const SERVICE_NAME = "breadcrumbAdapter"; // Constant for service name

interface IBreadcrumbAdapter {
  breadCrumbsContent: any;
  configuration: any;
  cmsPageName: string;
  pages: any;
  cms_type?: any;
}

export const breadcrumbAdapter = ({
  breadCrumbsContent,
  configuration,
  cmsPageName,
  pages,
  cms_type
}: IBreadcrumbAdapter): any => {
  try {
    logAdapterEvent(SERVICE_NAME, "Starting breadcrumb adaptation", {
      cmsPageName,
      cmsType: cms_type
    });

    const host = configuration?.breadcrumbHomeURL || "https://www.directv.com";
    const linkArr = [
      {
        content:
          (pages && pages.homepage && pages.homepage.breadcrumbTitle) || "Home",
        href: host,
        route: false
      }
    ];
    const hierarchyArray = [];
    try {
      if (pages && typeof pages === "object") {
        let formattedUrlArray = [];
        const pageArr = cmsPageName.split("/");
        if (pageArr.length > 1) {
          for (let j = 0; j < pageArr.length; j += 1) {
            if (j === 0) {
              formattedUrlArray.push(pageArr[j]);
            } else {
              let tempString = "";
              for (let i = 0; i <= j; i += 1) {
                if (i === 0) {
                  tempString = pageArr[i];
                } else {
                  tempString += `/${pageArr[i]}`;
                }
              }
              formattedUrlArray.push(tempString);
            }
          }
        } else {
          formattedUrlArray = pageArr;
        }

        let href = "";
        formattedUrlArray.forEach((item, index) => {
          let linkText = "";
          const itemFound: any = Object.entries(pages).find(item1 => {
            let itemFoundFlag = false;
            itemFoundFlag = item === item1[0];
            if (!itemFoundFlag && cms_type === "sb") {
              const newItem = item?.replace("sb/", "");
              itemFoundFlag = newItem === item1[0];
            }
            return itemFoundFlag;
          });

          if (itemFound === undefined && cmsPageName === "sports/nfl") {
            linkArr.push({
              content: "Sports",
              href: "https://www.directv.com/satellite/sports",
              route: false
            });
          }
          if (itemFound && itemFound[1]) {
            let breadCrumbTitle = itemFound[1].breadcrumbTitle;
            if (cms_type === "sb" && formattedUrlArray?.length - 1 === index) {
              breadCrumbTitle = breadCrumbsContent?.breadcrumbTitle;
            }

            // GitHub Copilot - START: normalize breadcrumb hierarchy and titles (preserve case)
            if (breadCrumbTitle && breadCrumbTitle.includes("</a>")) {
              const hierarchyElements = breadCrumbTitle.split("</a>");
              hierarchyElements.forEach((hierarchyItem, hIdx) => {
                if (hIdx !== hierarchyElements.length - 1) {
                  const normalized = hIdx !== 0 ?
                    `${hierarchyItem}</a>`.replace(/\//g, "-") :
                    `${hierarchyItem}</a>`;
                  hierarchyArray.push(normalized);
                } else {
                  // normalize final breadcrumb title: replace slashes -> dashes, strip edge dashes, trim
                  breadCrumbTitle = hierarchyItem
                    .replace(/\//g, "-")
                    .replace(/^-+|-+$/g, "")
                    .trim();
                }
              });
            }
            // GitHub Copilot - END

            let currentItem = item;
            try {
              // Quick Fix as breadCrumbTitle is not present in SB
              if (currentItem?.indexOf("sb/") !== -1) {
                currentItem = currentItem.replace(/sb\//g, "");
              }
            } catch (err) {
              logError(SERVICE_NAME, err, { item: currentItem });
            }
            let title = breadCrumbTitle;
            if (
              !Array.isArray(breadCrumbTitle) &&
              /\[(.*?)\]/gim.test(breadCrumbTitle)
            ) {
              [, title] = breadCrumbTitle.split(",");
            } else if (Array.isArray(breadCrumbTitle)) {
              [, title] = breadCrumbTitle;
            }

            // GitHub Copilot - START: clean and normalize title (preserve case)
            title = title?.replace(/"|\[|\]/g, "");
            title = !title ? cms_type === "sb" && pages[`sb/${currentItem}`]?.breadcrumbTitle : title;
            // ensure any remaining slashes become dashes and trim edge dashes
            title = String(title || "")
              .replace(/\//g, "-")
              .replace(/^-+|-+$/g, "")
              .trim();
            // GitHub Copilot - END

            linkText = title || pageArr[index] || "";
            if (linkText !== "") {
              href = `${host}/${currentItem}/`;
              // GitHub Copilot - START: canonicalize breadcrumb content -> replace slashes with dashes (preserve case)
              const normalizedContent = String(linkText)
                .replace(/\//g, "-")
                .replace(/^-+|-+$/g, "")
                .trim();
              linkArr.push({
                content: normalizedContent,
                href,
                route: false
              });
              // GitHub Copilot - END
            }
          }
        });
      }
      logAdapterEvent(
        SERVICE_NAME,
        "Breadcrumb adaptation completed successfully",
        {
          linksCount: linkArr?.length,
          hierarchyArrayLength: hierarchyArray?.length
        }
      );
    } catch (error) {
      logError(SERVICE_NAME, error, {
        cmsPageName,
        cmsType: cms_type
      });
    }

    const analytics = {
      componentName: `BREADCRUMBS`,
      contentFriendlyName: `BREADCRUMBS`,
      contentProductGroup: ``,
      contentSystem: "AEM"
    };

    const newBreadCrumbsContent = {
      props: {
        analytics,
        links: linkArr,
        hierarchyArray,
        options: {
          id: ComponentNames.BREADCRUMBS
        },
        isDoubleBreadcrumbNeeded: breadCrumbsContent?.isDoubleBreadcrumbNeeded
      },
      type: ComponentNames.BREADCRUMBSV2
    };

    return newBreadCrumbsContent;
  } catch (err) {
    logError(SERVICE_NAME, err, {
      cmsPageName,
      cmsType: cms_type
    });
    return null;
  }
};

export default breadcrumbAdapter;
